import { useEffect, useState } from 'react'
import ReactModal from 'react-modal'
import '../../../styles/modals/FeedBackModal.css'
import '../../../styles/modals/StudentFeedbackModal.css'
import { ReactComponent as CloseModalIcon } from '../../../assets/svg/CloseModalIcon.svg'
import { useTranslation } from 'react-i18next'
import { useUser } from '../../../auth'
import { useSocket } from '../../../context/socket'
import { notify } from '../../../utils/util'
import {
    getArchive,
    getDayArchive,
    makeAssignment,
    getTeachersForAssignment,
    sendSeenFeedbackComment,
    takeActionFeedbackComment,
    getById,
    getSuperiorTeachers,
    sendNotification,
    createCustomAlert,
} from '../../../server/server'
import FeedbackHistoryView from './FeedbackHistoryView'
import FeedbackDayHeader from './FeedbackDayHeader'
import FeedbackActionBar from './FeedbackActionBar'
import FeedbackDayBody from './FeedbackDayBody'
import AssignActionPanel from './AssignActionPanel'
import TakeActionPanel from './TakeActionPanel'
import MessageContainer from './MessageContainer'
import { ANALYTICS_EVENTS } from '../../../analytics/events'
import useTrackedSurface from '../../../analytics/useTrackedSurface'

const MODAL_STYLE = {
    overlay: {
        zIndex: 100,
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        backdropFilter: 'blur(5px)',
    },
    content: {
        padding: 0,
        margin: 'auto',
        paddingTop: 20,
        opacity: 1,
        inset: 0,
        width: 600,
        backgroundColor: 'transparent',
        maxHeight: '100vh',
        height: '100vh',
        border: 'none',
        borderRadius: 10,
        boxShadow: 'none',
        overflow: 'hidden',
        position: 'relative',
    },
}

const normalizeFeedback = (data) => ({
    ...data,
    takenActionBy: data?.actionTakenBy?.map((e) => e.id) ?? [],
})

function FeedBackModal({ modalIsOpen, closeModal, student, day, userType = 'student', analyticsSource = 'classroom' }) {
    const { t } = useTranslation()
    const { user } = useUser()
    const isStudent = userType === 'student'
    const { socket } = useSocket()

    const [studentUser, setStudentUser] = useState({})
    const [feedback, setFeedback] = useState({})
    const [feedbackDay, setFeedbackDay] = useState(day)
    const [actionTaken, setActionTaken] = useState(false)

    const [profileState, setProfileState] = useState('day')
    const [currentState, setCurrentState] = useState('init')

    const [archiveContent, setArchiveContent] = useState([])
    const [loadingContent, setLoadingContent] = useState(true)

    const [historyList, setHistoryList] = useState([])
    const [fullHistorySize, setFullHistorySize] = useState(-1)
    const [hasMore, setHasMore] = useState(true)
    const [loadingHistory, setLoadingHistory] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)

    const [loadedTeachers, setLoadedTeachers] = useState([])
    const [superiorTeachers, setSuperiorTeachers] = useState({})
    const [processingAction, setProcessingAction] = useState(false)

    const [creatingAlert, setCreatingAlert] = useState(false)
    const isCatchUp = analyticsSource === 'catch_up'
    const contentId = isCatchUp ? student?.feedback?.id : student?.user?.id
    const content = `${student?.user?.first_name || ''} ${student?.user?.last_name || ''}`.trim()
    useTrackedSurface(isCatchUp ? ANALYTICS_EVENTS.CATCH_UP_STUDENT_FEEDBACK_VIEWED : ANALYTICS_EVENTS.CLASSROOM_STUDENT_FEEDBACK_VIEWED, {
        active: modalIsOpen,
        contentId,
        content,
    })
    useTrackedSurface(ANALYTICS_EVENTS.CATCH_UP_STUDENT_FEEDBACK_HISTORY_VIEWED, {
        active: modalIsOpen && isCatchUp && profileState === 'history',
        contentId,
        content,
    })
    useTrackedSurface(isCatchUp ? ANALYTICS_EVENTS.CATCH_UP_STUDENT_FEEDBACK_SEND_MESSAGE_VIEWED : ANALYTICS_EVENTS.CLASSROOM_STUDENT_FEEDBACK_SEND_MESSAGE_VIEWED, {
        active: modalIsOpen && currentState === 'sendPrivateMessage',
        contentId,
        content,
    })
    useTrackedSurface(isCatchUp ? ANALYTICS_EVENTS.CATCH_UP_STUDENT_FEEDBACK_ASSIGN_ACTION_VIEWED : ANALYTICS_EVENTS.CLASSROOM_STUDENT_FEEDBACK_ASSIGN_ACTION_VIEWED, {
        active: modalIsOpen && currentState === 'assignAction',
        contentId,
        content,
    })
    useTrackedSurface(isCatchUp ? ANALYTICS_EVENTS.CATCH_UP_STUDENT_FEEDBACK_TAKE_ACTION_VIEWED : ANALYTICS_EVENTS.CLASSROOM_STUDENT_FEEDBACK_TAKE_ACTION_VIEWED, {
        active: modalIsOpen && currentState === 'takingAction',
        contentId,
        content,
    })

    // ─── Data loading ────────────────────────────────────────────────────────

    useEffect(() => {
        setActionTaken(student.feedback?.actionTakenBy?.map((e) => e.id).includes(user.id))
        setStudentUser(student.user)
        setFeedback(normalizeFeedback(student.feedback))
        setFeedbackDay(day)
        loadArchiveStudent()
        if (student.feedback?.emotion) sendSeenComment()
    }, [student])

    useEffect(() => {
        if (modalIsOpen) {
            loadTeachers()
            loadSuperiorTeachers()
            setProfileState('day')
        }
    }, [modalIsOpen])

    useEffect(() => {
        ReactModal.setAppElement('#root')
    }, [])

    // Blur the page behind the modal while it is open and, crucially, un-blur
    // it again on close (this used to only ever add the class).
    useEffect(() => {
        const home = document.getElementById('home')
        if (!home) return undefined
        home.classList.toggle('blurred', modalIsOpen)
        return () => home.classList.remove('blurred')
    }, [modalIsOpen])

    const loadTeachers = async () => {
        const resp = await getTeachersForAssignment({ student_id: student.user?.id })
        if (resp.status === 200) {
            setLoadedTeachers(resp?.data)
        }
    }

    const loadSuperiorTeachers = async () => {
        const resp = await getSuperiorTeachers(student.user)
        if (resp.status === 200) {
            if (resp.data.headOfClass || resp.data.headOfYear) setSuperiorTeachers(resp.data)
        }
    }

    const loadFeedbackData = async () => {
        const resp = await getById({ collection: 'feedback', id: student?.feedback?.id })
        if (resp.status === 200) {
            setFeedback(normalizeFeedback(resp.data))
        }
    }

    const loadArchiveStudent = async () => {
        if (student.user === undefined) return
        const resp = await getDayArchive({ user_id: student.user?.id, number_of_archives: 0, day: feedbackDay })
        if (resp.status === 200) {
            setArchiveContent(resp.data.data)
            setLoadingContent(false)
        }
    }

    const sendSeenComment = async () => {
        const resp = await sendSeenFeedbackComment({ user_id: user.id, feedback_id: student.feedback?.id })
        if (resp.status === 200) {
            const data = resp.data
            setActionTaken(!!data.actionTakenBy?.map((e) => e.id).includes(user.id))
            setFeedback(normalizeFeedback(data))
        } else {
            console.log('Send Seen error: ', resp)
        }
    }

    const loadStudentHistory = async () => {
        const resp = await getArchive({ user_id: studentUser.id, number_of_archives: historyList.length, collection: 'answers' })
        if (resp.status === 200) {
            const respData = resp.data
            setFullHistorySize(respData.size)
            if (historyList.length === 0) {
                setLoadingHistory(true)
                setHistoryList(respData.data)
            } else {
                setHistoryList((prev) => [...prev, ...respData.data])
            }
            setHasMore(respData.size > historyList.length)
            setLoadingMore(false)
        }
        setLoadingHistory(false)
    }

    // ─── Navigation ──────────────────────────────────────────────────────────

    const openHistory = () => {
        setProfileState('history')
        loadStudentHistory()
    }

    const closeHistory = () => {
        setHistoryList([])
        setFullHistorySize(-1)
        setProfileState('day')
    }

    const loadNextHistory = () => {
        if (historyList.length < fullHistorySize) {
            setLoadingMore(true)
            loadStudentHistory()
        } else {
            setHasMore(false)
        }
    }

    // ─── Action bar handlers ─────────────────────────────────────────────────

    const pressSendMessageButton = () => {
        setCurrentState(currentState === 'sendPrivateMessage' ? 'init' : 'sendPrivateMessage')
    }

    const pressAssignAction = () => {
        setCurrentState(currentState === 'assignAction' ? 'init' : 'assignAction')
    }

    const pressTakeAction = () => {
        if (!actionTaken) {
            setCurrentState(currentState === 'takingAction' ? 'init' : 'takingAction')
        }
    }

    // ─── Panel submit handlers ───────────────────────────────────────────────

    const assignAction = async (selectedTeacher, message) => {
        let data = { teacher: selectedTeacher, message }
        setProcessingAction(true)
        if (!!feedback?.id) data['feedback_id'] = feedback?.id
        else data['student_id'] = studentUser?.id
        const resp = await makeAssignment(data)
        if (resp.status === 200) {
            notify(t('actionHasBeenAssignedToTeacher'))
            socket?.emit('make_action_assignment', resp.data)
            loadFeedbackData()
            sendNotification({ data: { assignedTeacher: selectedTeacher, type: 'assignment' } })
            if (!feedback.id) closeModal()
        } else {
            notify(t('assignActionFailed'), 'error')
        }
        setCurrentState('init')
        setProcessingAction(false)
    }

    const takeAction = async (message) => {
        const resp = await takeActionFeedbackComment({ user_id: user.id, feedback_id: feedback.id, user_message: message })
        if (resp.status === 200) {
            const data = resp.data
            setActionTaken(!!data.actionTakenBy?.map((e) => e.id).includes(user.id))
            setFeedback(normalizeFeedback(data))
        }
        setCurrentState('init')
    }

    const messageSent = () => {
        setActionTaken(true)
    }

    const createAlert = async () => {
        setCreatingAlert(true)
        const resp = await createCustomAlert({ feedback_id: feedback.id })
        if (resp.status === 200) {
            loadFeedbackData()
        }
        setCreatingAlert(false)
    }

    // ─── Render ──────────────────────────────────────────────────────────────

    return (
        <ReactModal
            ariaHideApp={false}
            transparent
            closeTimeoutMS={600}
            isOpen={modalIsOpen}
            onAfterClose={() => {
                setHistoryList([])
                setCurrentState('init')
            }}
            onRequestClose={closeModal}
            style={MODAL_STYLE}
        >
            <div
                style={{
                    position: 'absolute',
                    top: 20,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <div id="close-modal-button" className={`feedback-modal-icon shadow-2xl ${profileState === 'history' && 'bg-darkPurple'}`} onClick={closeModal}>
                    <CloseModalIcon />
                </div>
            </div>

            {profileState === 'history' && (
                <FeedbackHistoryView
                    historyList={historyList}
                    hasMore={hasMore}
                    loadingMore={loadingMore}
                    studentName={`${student.user?.first_name} ${student.user?.last_name}`}
                    onClose={closeHistory}
                    onLoadNext={loadNextHistory}
                />
            )}

            {profileState === 'day' && (
                <div className="feedback-student-modal" style={{ overflow: 'visible', maxHeight: '78vh' }}>
                    <FeedbackDayHeader
                        emotion={student.feedback?.emotion}
                        studentFirstName={student.user?.first_name}
                        studentLastName={student.user?.last_name}
                        feedbackDay={feedbackDay}
                        studentUserId={studentUser?.id}
                        isStudent={isStudent}
                        reasons={student?.feedback?.reasons}
                        onOpenHistory={openHistory}
                    />
                    <div className="pb-5 bg-[#fafaff] rounded-[10px]">
                        <div className="feedback-modal-body">
                            <FeedbackActionBar
                                currentState={currentState}
                                actionTaken={actionTaken}
                                hasEmotion={!!student.feedback?.emotion}
                                isPrimary={!!student?.feedback?.primary}
                                isStudent={isStudent}
                                onPressSendMessage={pressSendMessageButton}
                                onPressAssignAction={pressAssignAction}
                                onPressTakeAction={pressTakeAction}
                            />
                            {currentState === 'init' && (
                                <FeedbackDayBody
                                    feedback={feedback}
                                    archiveContent={archiveContent}
                                    loadingContent={loadingContent}
                                    creatingAlert={creatingAlert}
                                    onCreateAlert={createAlert}
                                />
                            )}
                            {currentState === 'assignAction' && (
                                <AssignActionPanel
                                    loadedTeachers={loadedTeachers}
                                    superiorTeachers={superiorTeachers}
                                    processingAction={processingAction}
                                    onSubmit={assignAction}
                                />
                            )}
                            {currentState === 'takingAction' && <TakeActionPanel onSubmit={takeAction} />}
                            {currentState === 'sendPrivateMessage' && <MessageContainer setCurrentState={setCurrentState} feedback={feedback} messageSent={messageSent} />}
                        </div>
                    </div>
                </div>
            )}
        </ReactModal>
    )
}

export default FeedBackModal
